import type { MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'
import { ArrowRight } from 'lucide-react'
import { buttonVariants } from '#app/components/ui/button'
import { cn } from '#app/utils/misc'
import { siteConfig } from '#app/utils/constants/brand'
import { ROUTE_PATH as LOGIN_PATH } from '#app/routes/auth+/login'

export const ROUTE_PATH = '/email-to-webhook' as const

const TITLE = 'Email to Webhook — Forward Inbound Email as JSON | Inbox Bridge'
const DESCRIPTION =
  'Receive email at a webhook in seconds. Inbox Bridge parses inbound email into structured JSON and POSTs it to your endpoint with retries and replay.'
const URL = `${siteConfig.siteUrl}/email-to-webhook`

const BREADCRUMB_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: siteConfig.siteUrl },
    { '@type': 'ListItem', position: 2, name: 'Email to Webhook', item: URL },
  ],
}

export const meta: MetaFunction = () => {
  return [
    { title: TITLE },
    { name: 'description', content: DESCRIPTION },
    { tagName: 'link', rel: 'canonical', href: URL },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: URL },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESCRIPTION },
    { property: 'og:image', content: siteConfig.siteImageUrl },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: TITLE },
    { name: 'twitter:description', content: DESCRIPTION },
    { 'script:ld+json': BREADCRUMB_JSON_LD },
  ]
}

export default function EmailToWebhook() {
  return (
    <main className="mx-auto flex w-full max-w-screen-md flex-col gap-8 px-6 py-20">
      <header className="flex flex-col gap-4">
        <h1 className="text-4xl font-bold tracking-tight text-primary md:text-5xl">
          Email to Webhook
        </h1>
        <p className="text-lg leading-relaxed text-muted-foreground">
          Turn every inbound email into a structured JSON webhook. Get a unique email
          address, point it at your API, and ship.
        </p>
      </header>

      <section className="flex flex-col gap-4">
        <h2 className="text-2xl font-semibold text-primary">
          What is an email-to-webhook service?
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          An email-to-webhook service receives inbound email on your behalf, parses the
          subject, body, headers, and attachments into a JSON payload, then delivers it
          to an HTTPS endpoint you control. You never run an SMTP server or poll an
          inbox — email becomes just another webhook event.
        </p>
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-2xl font-semibold text-primary">
          Why use Inbox Bridge to forward email to a webhook?
        </h2>
        <ul className="flex list-disc flex-col gap-2 pl-6 leading-relaxed text-muted-foreground">
          <li>Unique hash-based inbound addresses so your endpoints stay private.</li>
          <li>Automatic retries with exponential backoff on delivery failure.</li>
          <li>One-click replay of any delivery from the dashboard.</li>
          <li>Clean JSON payload: subject, from, to, text, HTML, headers, attachments.</li>
          <li>Per-bridge delivery logs with response code and duration.</li>
        </ul>
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-2xl font-semibold text-primary">How it works</h2>
        <ol className="flex list-decimal flex-col gap-2 pl-6 leading-relaxed text-muted-foreground">
          <li>Create a bridge and copy the generated inbound email address.</li>
          <li>Point your webhook URL at your API endpoint.</li>
          <li>Send or forward email — receive the parsed JSON at your endpoint.</li>
        </ol>
      </section>

      <div className="flex gap-3">
        <Link
          to={LOGIN_PATH}
          className={cn(buttonVariants({ size: 'lg' }), 'gap-2 font-semibold')}>
          Start for free
          <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/guides/inbound-email-webhook"
          className={cn(buttonVariants({ variant: 'outline', size: 'lg' }))}>
          Read the tutorial
        </Link>
      </div>

      <section className="mt-8 border-t border-border/40 pt-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-primary">
          Related
        </h2>
        <ul className="flex flex-col gap-2 text-base text-muted-foreground">
          <li>
            <Link
              to="/guides/inbound-email-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Inbound email webhook tutorial
            </Link>
            {' — '}step-by-step guide to parsing inbound email into JSON.
          </li>
          <li>
            <Link
              to="/guides/receive-inbound-email-as-json-nextjs"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Receive inbound email as JSON in Next.js
            </Link>
            {' — '}framework-specific guide with route handler examples.
          </li>
          <li>
            <Link
              to="/guides/forward-email-to-slack-without-zapier"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Forward email to Slack (without Zapier)
            </Link>
            {' — '}post formatted, filtered email into a Slack channel.
          </li>
          <li>
            <Link
              to="/cloudmailin-alternative"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              CloudMailin alternative
            </Link>
            {' — '}feature and pricing comparison.
          </li>
        </ul>
      </section>
    </main>
  )
}
